/**
 * editHistory.js — the undo/redo stack transition, as a pure function.
 *
 * Extracted so the rules that were broken can be tested without mounting a 10,000-line form:
 *
 *   1. ONE transition. The old code truncated the stack using the closure value of the
 *      index while incrementing the index functionally, so two pushes in one React batch
 *      (which a blur-reformat immediately after a keystroke reliably produces) computed
 *      their new index from the same stale number and one overwrote the other.
 *   2. A SEED entry. Nothing seeded entry 0 with the state as loaded, so the first push of
 *      a session recorded the POST-change state at index 0 — leaving the user's first edit
 *      permanently un-undoable and every later undo off by one.
 *   3. A CAP and COALESCING (H5). Every keystroke deep-cloned the whole items array onto a
 *      stack with no ceiling and no grouping, and blur added a second push per numeric
 *      field. On a 100-item bill over a long session that is hundreds of megabytes and
 *      visible input lag — and undo was useless anyway, because getting past "2450" took
 *      four presses. Both policies live HERE, in one place, rather than in the form.
 */

/**
 * Hard ceiling on remembered states. 100 is far more undo than anyone uses in one bill, and
 * it bounds the memory a long session can reach regardless of how big the items array is.
 * Beyond it the OLDEST state is dropped — losing the far past is the only acceptable loss.
 */
export const MAX_HISTORY = 100;

/**
 * S5 — structuredClone, with the old JSON clone as a fallback.
 *
 * JSON.parse(JSON.stringify(x)) drops `undefined` values and turns a Date into a string.
 * Nothing on an item row is a Date today, which is precisely why this is worth fixing now:
 * the day one is added, undo would start silently corrupting rows instead of failing.
 * Feature-detected once, not per call.
 */
const hasStructuredClone = typeof structuredClone === 'function';
const clone = (items) => {
  const value = items || [];
  if (hasStructuredClone) {
    try {
      return structuredClone(value);
    } catch (e) {
      // A non-cloneable value (a function, a DOM node) landed on a row. Fall through to the
      // lossy clone rather than throwing away the user's undo stack.
    }
  }
  return JSON.parse(JSON.stringify(value));
};

/**
 * Do these two pushes describe continued typing in the SAME box?
 *
 * Only then may the newer one replace the older, so "2450" is one undo step rather than
 * four. A push carrying no metadata never coalesces (a bulk fill, a revert, a row delete —
 * each of those is a step in its own right), and neither does one marked `boundary`, which
 * is how a blur-reformat avoids swallowing the edit that follows it on another field.
 */
const canCoalesce = (topMeta, meta) => (
  Boolean(topMeta) && Boolean(meta)
  && !topMeta.boundary && !meta.boundary
  && topMeta.rowId != null && meta.rowId != null
  && topMeta.rowId === meta.rowId
  && topMeta.field === meta.field
);

/**
 * @param {{stack: Array, index: number, meta?: Array}} prev
 * @param {Array} nextItems state AFTER the change
 * @param {Array|null} seedWith state BEFORE the change; used only when the stack is empty
 * @param {{rowId: string|number, field: string, boundary?: boolean}|null} meta
 *        what this push changed. Consecutive pushes with matching metadata collapse into
 *        one entry. Omit it for anything that is not a single-field edit.
 * @returns {{stack: Array, index: number, meta: Array}} always index === stack.length - 1
 */
export const pushHistoryState = (prev, nextItems, seedWith = null, meta = null) => {
  const base = prev && Array.isArray(prev.stack) ? prev : { stack: [], index: -1, meta: [] };
  const baseMeta = Array.isArray(base.meta) ? base.meta : [];

  // Anything ahead of the cursor is a redo branch the user has just abandoned.
  const stack = base.stack.slice(0, base.index + 1);
  const metas = baseMeta.slice(0, base.index + 1);

  if (stack.length === 0 && seedWith) {
    stack.push(clone(seedWith));
    metas.push(null);                 // the seed is never a coalescing target
  }

  // Coalesce only at the top of the stack, and never over the seed — entry 0 is the state
  // the user must always be able to get back to, and replacing it would delete exactly the
  // pre-typing value this feature exists to restore in one press.
  const atTop = base.index === base.stack.length - 1;
  if (atTop && stack.length >= 2 && canCoalesce(metas[metas.length - 1], meta)) {
    stack[stack.length - 1] = clone(nextItems);
    metas[metas.length - 1] = meta;
  } else {
    stack.push(clone(nextItems));
    metas.push(meta);
  }

  // Cap: drop the oldest states. Index is derived from the stack, so it cannot drift.
  while (stack.length > MAX_HISTORY) {
    stack.shift();
    metas.shift();
  }

  return { stack, index: stack.length - 1, meta: metas };
};

/**
 * The same clone the stack uses, exported so callers holding their OWN snapshot of the items
 * (StockInForm's Revert All / per-row Reset snapshots) copy them by exactly the same rules.
 * Two clone implementations is two chances to lose a field.
 */
export const cloneItemsDeep = clone;

export const canUndoHistory = (h) => Boolean(h) && h.index > 0;
export const canRedoHistory = (h) => Boolean(h) && h.index < h.stack.length - 1;

export const EMPTY_HISTORY = { stack: [], index: -1, meta: [] };
